import { z } from 'zod';

import { memberIdSchema } from '../identity/legacy-identity-id';
import { nrtIsoInstantSchema } from '../nrt/instant';
import { nrtSuccessEnvelope } from '../nrt/responses';
import { nrtOrderStatusSchema } from '../nrt/vocabularies';
import { memberOrderSourceSchema } from './orders';

/**
 * One order in a member's staff order list: `GET /api/v1/internal/orders/members/{memberId}`.
 *
 * Every order of the program is listed, whatever its source, because a
 * staff-created order has no request row and the request queue never sees it.
 *
 * `status` is nullable, unlike the single-order read: an order imported from
 * 1.0 with no status events has no derivable status, and this list is the one
 * place staff see it. Tightening it later would break every parser already
 * reading it (ADR-135 Rule 3), so it is nullable from the first release.
 *
 * `historical` is the server's answer to "is this open work?". An imported
 * order no request points at is history even when it sits at `created`, and
 * the create route's pending check uses the same rule, so the client never
 * re-derives it.
 *
 * No address, address id or reject reason: those are served only by the
 * single-order read, opened for one order on purpose (data minimisation).
 * Items carry no quantity bound or non-empty rule, because a historical order
 * may be item-less at any status.
 */
export const staffMemberOrderListItemSchema = z.object({
  orderId: z.string().uuid(),
  requestId: z.string().uuid().nullable(),
  memberId: memberIdSchema,
  programKey: z.string(),
  source: memberOrderSourceSchema,
  status: nrtOrderStatusSchema.nullable(),
  historical: z.boolean(),
  items: z.array(z.object({ sku: z.string(), quantity: z.number().int().min(1) })),
  placedAt: nrtIsoInstantSchema,
  statusHistory: z.array(
    z.object({ status: nrtOrderStatusSchema, occurredAt: nrtIsoInstantSchema }),
  ),
});
export type StaffMemberOrderListItem = z.infer<typeof staffMemberOrderListItemSchema>;

/** Newest `placedAt` first. `nextCursor` is null on the last page. */
export const staffMemberOrderListDataSchema = z.object({
  orders: z.array(staffMemberOrderListItemSchema),
  nextCursor: z.string().nullable(),
});
export type StaffMemberOrderListData = z.infer<typeof staffMemberOrderListDataSchema>;

export const staffMemberOrderListResponseSchema = nrtSuccessEnvelope(
  staffMemberOrderListDataSchema,
);
export type StaffMemberOrderListResponse = z.infer<typeof staffMemberOrderListResponseSchema>;
